import { useEffect, useState, useSyncExternalStore } from 'react';

/** Subscribe to a CSS media query (false when `matchMedia` is unavailable, e.g. SSR/tests). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      const mq = typeof window !== 'undefined' ? window.matchMedia?.(query) : undefined;
      mq?.addEventListener?.('change', notify);
      return () => mq?.removeEventListener?.('change', notify);
    },
    () => (typeof window !== 'undefined' ? !!window.matchMedia?.(query).matches : false),
    () => false,
  );
}

/** Below the `lg` breakpoint: the single-column phone/tablet layout. */
export const useIsPhone = () => useMediaQuery('(max-width: 1023px)');
/** Primary pointer is a finger: no hover, no Shift key. */
export const useCoarsePointer = () => useMediaQuery('(pointer: coarse)');

/**
 * Pixels of the layout viewport covered by the on-screen keyboard (0 when closed or unsupported).
 * Pair with `interactive-widget=resizes-content` (index.html) so Android and iOS agree.
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setInset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
  return inset;
}
